import type { MiioErrors } from '~/types/errors'
export function useMiioErrors(): MiioErrors {
  return {
    version: 12,
    '01-0001': {
      reason: "Checkout: The basket hasn't a shipping address.",
      'extra-args': [],
      title: {
        pt: 'Não consta a sua morada',
        en: 'Your address is not listed',
        es: 'Tu dirección no está en la lista',
        fr: "Votre adresse n'est pas répertoriée"
      },
      description: {
        pt: 'Por favor insira a sua morada de envio',
        en: 'Please enter your shipping address',
        es: 'Por favor, introduzca su dirección de envio',
        fr: "S'il vous plaît entrez votre adresse de livraison"
      },
      'send-sentry-alert': true
    },
    '01-0003': {
      reason: "Checkout: Can't use both coupon and points.",
      'extra-args': [],
      title: {
        pt: 'Terá de escolher',
        en: "You'll have to choose",
        es: 'Tendrás que elegir',
        fr: 'Vous devrez choisir'
      },
      description: {
        pt: 'Não pode utilizar cupão de desconto e trocar pontos na mesma compra. Escolha apenas um para continuar.',
        en: "You can't use a discount coupon and exchange points in the same order. Please choose one to continue.",
        es: 'No puedes usar un cupón de descuento y canjear puntos por la misma compra. Elija solo uno para continuar.',
        fr: 'Vous ne pouvez pas utiliser un coupon de réduction et échanger des points pour un même achat. Choisissez-en un seul pour continuer.'
      }
    },
    '01-0004': {
      reason: 'Checkout: The voucher applied to your basket is invalid',
      'notes-to-frontend': 'When this happens, remove the current voucher from the basket',
      'extra-args': [],
      title: {
        pt: 'O seu cupão de desconto foi removido.',
        en: 'Your discount coupon has been removed.',
        es: 'Su cupón de descuento ha sido eliminado.',
        fr: 'Votre bon de réduction a été supprimé.'
      },
      description: {
        pt: 'O cupão deixou de ser válido e portanto foi removido do seu cesto.',
        en: 'The coupon is no longer valid and has therefore been removed from your basket.',
        es: 'El cupón ya no es válido y, por lo tanto, se ha eliminado de su cesta.',
        fr: "Le coupon n'est plus valable et a donc été retiré de votre panier."
      }
    },
    '01-0005': {
      reason: 'Checkout: Cannot checkout with empty basket',
      'extra-args': [],
      title: {
        pt: 'Não tem itens no seu cesto',
        en: 'There are no items in your basket',
        es: 'No hay artículos en su cesta',
        fr: "Il n'y a aucun article dans votre panier"
      },
      description: {
        pt: 'Escolha um produto primeiro, e adicione-o ao cesto para continuar.',
        en: 'Please choose a product first, then add it to your cart to continue.',
        es: 'Primero elija un producto y luego agréguelo a su carrito para continuar.',
        fr: "Veuillez d'abord choisir un produit, puis l'ajouter à votre panier pour continuer."
      }
    },
    '01-0006': {
      reason: 'Checkout: Shipping price incorrect %s != %s',
      'extra-args': ['price_from_request', 'price_from_basket'],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0007': {
      reason: 'Checkout: Total incorrect %s != %s',
      'extra-args': ['price_from_request', 'price_from_basket'],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0008': {
      reason: "Checkout: Can't use more points than available for this basket.",
      'extra-args': ['max_points'],
      title: {
        pt: 'Número máximo de pontos',
        en: 'Maximum number of points',
        es: 'Número máximo de puntos',
        fr: 'Nombre maximal de points'
      },
      description: {
        pt: 'Pode usar até $max_points nesta compra',
        en: 'You can use up to $max_points on this purchase',
        es: 'Puedes usar hasta $max_points en esta compra',
        fr: "Vous pouvez utiliser jusqu'à $max_points pour cet achat"
      }
    },
    '01-0009': {
      reason: 'Add voucher: Voucher code unknown',
      'extra-args': ['voucher_code'],
      title: {
        pt: 'Este cupão não é válido ou está incorreto',
        en: 'This coupon is invalid or incorrect',
        es: 'Este cupón no es válido o es incorrecto',
        fr: 'Ce coupon est invalide ou incorrect'
      },
      description: {
        pt: 'Verifique se o código $voucher_code foi escrito corretamente ou tente inserir um novo cupão.',
        en: 'Check that the $voucher_code is spelled correctly or try entering a new coupon.',
        es: 'Verifica que el $voucher_code esté escrito correctamente o intenta ingresar un nuevo cupón.',
        fr: "Vérifiez que le $voucher_code est correctement orthographié ou essayez d'entrer un nouveau coupon."
      }
    },
    '01-0010': {
      reason: "Add voucher: The '%s' voucher has expired",
      'extra-args': ['voucher_code'],
      title: {
        pt: 'O código $voucher_code expirou',
        en: 'The $voucher_code has expired',
        es: 'El $voucher_code ha caducado',
        fr: 'Le $voucher_code a expiré'
      },
      description: {
        pt: 'Infelizmente este código não é mais válido, mas poderá sempre inserir um novo cupão.',
        en: 'Unfortunately this code is no longer valid, but you can always enter a new coupon.',
        es: 'Desafortunadamente, este código ya no es válido, pero siempre puede ingresar un nuevo cupón.',
        fr: "Malheureusement, ce code n'est plus valable, mais vous pouvez toujours entrer un nouveau coupon."
      }
    },
    '01-0011': {
      reason: 'Add voucher: Your basket does not qualify for a voucher discount.',
      'extra-args': [],
      title: {
        pt: 'Este cupão não é válido para os itens do seu cesto',
        en: 'This coupon is not valid for the items in your basket.',
        es: 'Este cupón no es válido para los artículos de su cesta.',
        fr: "Ce coupon n'est pas valable pour les articles de votre panier."
      },
      description: {
        pt: 'Este código é válido apenas para produtos específicos. Adicione os produtos da campanha e insira novamente o código.',
        en: 'This code is only valid for specific products. Add the campaign products and re-enter the code.',
        es: 'Este código solo es válido para productos específicos. Agregue los productos de la campaña y vuelva a ingresar el código.',
        fr: "Ce code n'est valable que pour des produits spécifiques. Ajoutez les produits de la campagne et ressaisissez le code."
      }
    },
    '01-0012': {
      reason: 'Checkout: currency and basket currency must be the same.',
      'extra-args': [],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0015': {
      reason: 'Checkout: point exchange rate not found for %s',
      'extra-args': ['currency'],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0016': {
      reason: 'Add voucher: This voucher is not available to user',
      'extra-args': [],
      title: {
        pt: 'Cupão de desconto inválido',
        en: 'Invalid discount coupon',
        es: 'Cupón de descuento no válido',
        fr: 'Bon de réduction invalide'
      },
      description: {
        pt: 'Não reúne as condições para aplicar este cupão de desconto.',
        en: 'You do not meet the conditions to apply this discount coupon.',
        es: 'No cumples las condiciones para aplicar este cupón de descuento.',
        fr: 'Vous ne remplissez pas les conditions pour appliquer ce coupon de réduction.'
      }
    },
    '01-0017': {
      reason: "Checkout: Unauthorized (for example using another user's basket)",
      'extra-args': [],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0018': {
      reason: 'Add product: product unavailable',
      'extra-args': [],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0019': {
      reason: 'Add product: a maximum of %s can be bought',
      'extra-args': ['total_available'],
      title: {
        pt: 'Quantidade não disponível',
        en: 'Quantity not available',
        es: 'Cantidad no disponible',
        fr: 'Quantité non disponible'
      },
      description: {
        pt: 'Apenas pode adicionar $total_available unidade(s) deste produto ao seu cesto.',
        en: 'You can only add $total_available unit(s) of this product to your cart.',
        es: 'Solo puede agregar $total_available disponible(s) de este producto a su carrito.',
        fr: 'Vous ne pouvez ajouter que $total_available disponible(s) de ce produit à votre panier.'
      },
      'send-sentry-alert': true
    },
    '01-0020': {
      reason: 'Add product: Due to technical limitations we are not able to ship more than %(threshold)d items in one order.',
      'extra-args': ['max_allowed', 'basket_threshold'],
      title: {
        pt: 'Quantidade não disponível',
        en: 'Quantity not available',
        es: 'Cantidad no disponible',
        fr: 'Quantité non disponible'
      },
      description: {
        pt: 'Devido a limitações técnicas, não nós é possível enviar mais do que $max_allowed unidade(s) na mesma encomenda.',
        en: 'Due to technical limitations, we are unable to ship more than $max_allowed unit(s) in the same order.',
        es: 'Debido a limitaciones técnicas, no podemos enviar más de $max_allowed unidad(es) en el mismo pedido.',
        fr: "En raison de limitations techniques, nous ne sommes pas en mesure d'expédier plus de $max_allowed unité(s) dans la même commande."
      },
      'send-sentry-alert': true
    },
    '01-0021': {
      reason: 'Remove product: The quantity to remove is superior to the quantity that is in the basket',
      'extra-args': ['desired_qty', 'current_qty'],
      title: {
        pt: 'Quantidade superior à existente no cesto',
        en: 'Quantity greater than existing in the basket',
        es: 'Cantidad mayor a la existente en la cesta',
        fr: 'Quantité supérieure à celle existant dans le panier'
      },
      description: {
        pt: 'Apenas pode remover $current_qty unidade(s) deste produto do seu cesto.',
        en: 'You can only remove $current_qty unit(s) of this product from your cart.',
        es: 'Solo puede eliminar $current_qty unidad(es) de este producto de su carrito.',
        fr: 'Vous ne pouvez supprimer que $current_qty unité(s) de ce produit de votre panier.'
      },
      'send-sentry-alert': true
    },
    '01-0022': {
      reason: "Checkout: Basket contains a product which isn't publicly available: {}",
      'extra-args': ['product_ids'],
      'notes-to-frontend':
        "product_ids will be a list of product ids that are currently on the basket. The idea with this error is to show a dialog with the copy below but then add the titles of the products separated by commas. A new button should also be available with the copy 'Atualizar pedido' that will remove the products that are no longer available and refresh the basket.",
      title: {
        pt: 'Produto(s) indisponível(eis)',
        en: 'Unavailable product(s)',
        es: 'Producto(s) no disponible(s)',
        fr: 'Produit(s) indisponible(s)'
      },
      description: {
        pt: 'O seu cesto contém artigo(s) que já não se encontra(m) disponível(eis) para venda. O(s) seguinte(s) produto(s) são afetado(s):',
        en: 'Your basket contains item(s) that are no longer available for sale. The following product(s) are affected:',
        es: 'Tu carrito contiene artículo(s) que ya no están disponibles para la venta. Los siguiente(s) producto(s) están afectados:',
        fr: 'Votre panier contient des article(s) qui ne sont plus disponibles à la vente. Les produit(s) suivant(s) sont concerné(s) :'
      }
    },
    '01-0026': {
      reason: 'Payment: The size of payment_method does not match the size of values',
      'extra-args': [],
      'notes-to-frontend': 'Show the option to contact customer support like on the generic error of the payment',
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0027': {
      reason: "Payment: Sum of 'values' list must be equal to 'total_value'.",
      'extra-args': [],
      'notes-to-frontend': 'Show the option to contact customer support like on the generic error of the payment',
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0028': {
      reason: 'Payment: total_value %s does not match with order total %s',
      'extra-args': ['total_value', 'order_total'],
      'notes-to-frontend': 'Show the option to contact customer support like on the generic error of the payment',
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0029': {
      reason: 'Payment: Something went wrong, payment could not be processed',
      'extra-args': [],
      'notes-to-frontend': 'Show the option to contact customer support like on the generic error of the payment',
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0031': {
      reason: 'Payment: Value %s for payment method %s is less than min capture %s.',
      'extra-args': ['value', 'payment_method', 'min_capture'],
      title: {
        pt: 'Não é possível efetuar o pagamento',
        en: 'Payment cannot be processed',
        es: 'No se puede procesar el pago',
        fr: 'Le paiement ne peut pas être traité'
      },
      description: {
        pt: 'Não é possível realizar um pagamento inferior a $min_capture €. Por favor, verifique o seu cesto.',
        en: 'It is not possible to make a payment less than $min_capture €. Please check your basket.',
        es: 'No es posible realizar un pago inferior a $min_capture €. Por favor, verifique su carrito.',
        fr: "Il n'est pas possible de faire un paiement inférieur à $min_capture €. Veuillez vérifier votre panier."
      },
      'send-sentry-alert': true
    },
    '01-0032': {
      reason: 'Payment: Order already paid.',
      'extra-args': [],
      'notes-to-frontend':
        'When the user leaves the dialog, the page should be refreshed (in case the user is in the order details page) or the user should be redirected to the order details page (in case the user is in the checkout flow).',
      title: {
        pt: 'Pagamento já efetuado',
        en: 'Payment already made',
        es: 'Pago ya realizado',
        fr: 'Paiement déjà effectué'
      },
      description: {
        pt: 'O pagamento deste pedido já foi efetuado. Se tiver alguma dúvida, por favor contacte o nosso serviço de apoio ao cliente.',
        en: 'The payment for this order has already been made. If you have any questions, please contact our customer support.',
        es: 'El pago de este pedido ya se ha realizado. Si tiene alguna pregunta, póngase en contacto con nuestro servicio de atención al cliente.',
        fr: 'Le paiement de cette commande a déjà été effectué. Si vous avez des questions, veuillez contacter notre service clientèle.'
      },
      'send-sentry-alert': true
    },
    '01-0033': {
      reason: 'Checkout: Your basket does not qualify for a points discount.',
      'extra-args': [],
      title: {
        pt: 'Não foi possível aplicar o desconto de pontos',
        en: 'Points discount could not be applied',
        es: 'No se pudo aplicar el descuento de puntos',
        fr: "La réduction de points n'a pas pu être appliquée"
      },
      description: {
        pt: 'O seu cesto não se qualifica para um desconto de pontos. Por favor, remova os pontos aplicados e tente novamente.',
        en: 'Your basket does not qualify for a points discount. Please remove the applied points and try again.',
        es: 'Tu carrito no se califica para un descuento de puntos. Por favor, elimina los puntos aplicados e inténtalo de nuevo.',
        fr: "Votre panier ne bénéficie pas d'une réduction de points. Veuillez supprimer les points appliqués et réessayer."
      },
      'send-sentry-alert': true
    },
    '01-0034': {
      reason: "Checkout: The basket doesn't have a billing address.",
      'extra-args': [],
      title: {
        pt: 'A sua morada de faturação está em falta',
        en: 'Your billing address is missing',
        es: 'Falta tu dirección de facturación',
        fr: 'Votre adresse de facturation est manquante'
      },
      description: {
        pt: 'Por favor insira a sua morada de faturação e tente novamente.',
        en: 'Please enter your billing address and try again.',
        es: 'Por favor, introduzca su dirección de facturación e inténtelo de nuevo.',
        fr: 'Veuillez saisir votre adresse de facturation et réessayer.'
      },
      'send-sentry-alert': true
    },
    '01-0035': {
      reason: "Checkout: The basket doesn't have tax.",
      'extra-args': [],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0036': {
      reason: 'Checkout: An error occurred while processing the order (general error).',
      'extra-args': [],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0037': {
      reason: 'Checkout: Something went wrong on subtracting points from %s: %s',
      'extra-args': ['order_id', 'error'],
      'show-generic-error': true,
      'send-sentry-alert': true
    },
    '01-0038': {
      reason: 'Checkout: Unable to calculate shipping price.',
      'extra-args': [],
      title: {
        pt: 'Não é possível realizar o pedido',
        en: 'Unable to place order',
        es: 'No se puede realizar el pedido',
        fr: 'Impossible de passer la commande'
      },
      description: {
        pt: 'Não realizamos envios para o país da morada de envio. Por favor, altere a morada ou contacte o apoio ao cliente.',
        en: 'We do not ship to the country of the shipping address. Please change the address or contact customer support.',
        es: 'No enviamos al país de la dirección de envío. Por favor, cambie la dirección o póngase en contacto con el servicio de atención al cliente.',
        fr: "Nous n'expédions pas vers le pays de l'adresse de livraison. Veuillez modifier l'adresse ou contacter le service client."
      }
    },
    '01-0039': {
      reason: 'Payment: Order is canceled.',
      'extra-args': [],
      title: {
        pt: 'Pedido encontra-se cancelado',
        en: 'Order is canceled',
        es: 'El pedido está cancelado',
        fr: 'La commande est annulée'
      },
      description: {
        pt: 'Não é possível efetuar o pagamento de um pedido cancelado. Se tiver alguma dúvida, por favor contacte o nosso serviço de apoio ao cliente.',
        en: 'It is not possible to make a payment for a canceled order. If you have any questions, please contact our customer support.',
        es: 'No es posible realizar un pago por un pedido cancelado. Si tiene alguna pregunta, póngase en contacto con nuestro servicio de atención al cliente.',
        fr: "Il n'est pas possible d'effectuer un paiement pour une commande annulée. Si vous avez des questions, veuillez contacter notre service clientèle."
      }
    },
    '01-0040': {
      reason: 'Checkout: The shipping address is invalid.',
      'extra-args': [],
      'notes-to-frontend': 'When the user closes the dialog, it should be redirected to the checkout addresses pages and the basket should be updated to make sure the addresses are still associated.',
      title: {
        pt: 'Morada de envio inválida',
        en: 'Invalid shipping address',
        es: 'Dirección de envío no válida',
        fr: 'Adresse de livraison invalide'
      },
      description: {
        pt: 'A morada de envio selecionada não é válida. Por favor, selecione uma morada de envio válida e tente novamente.',
        en: 'The selected shipping address is not valid. Please select a valid shipping address and try again.',
        es: 'La dirección de envío seleccionada no es válida. Por favor, seleccione una dirección de envío válida e inténtelo de nuevo.',
        fr: "L'adresse de livraison sélectionnée n'est pas valide. Veuillez sélectionner une adresse de livraison valide et réessayer."
      },
      'send-sentry-alert': true
    },
    '01-0041': {
      reason: 'Checkout: The billing address is invalid.',
      'extra-args': [],
      'notes-to-frontend': 'When the user closes the dialog, it should be redirected to the checkout addresses pages and the basket should be updated to make sure the addresses are still associated.',
      title: {
        pt: 'Morada de faturação inválida',
        en: 'Invalid billing address',
        es: 'Dirección de facturación no válida',
        fr: 'Adresse de facturation invalide'
      },
      description: {
        pt: 'A morada de faturação selecionada não é válida. Por favor, selecione uma morada de faturação válida e tente novamente.',
        en: 'The selected billing address is not valid. Please select a valid billing address and try again.',
        es: 'La dirección de facturación seleccionada no es válida. Por favor, seleccione una dirección de facturación válida e inténtelo de nuevo.',
        fr: "L'adresse de facturation sélectionnée n'est pas valide. Veuillez sélectionner une adresse de facturation valide et réessayer."
      },
      'send-sentry-alert': true
    },
    '01-0042': {
      reason: 'Checkout: The installation address is invalid.',
      'extra-args': [],
      title: {
        pt: 'Morada de instação inválida',
        en: 'Invalid installation address',
        es: 'Dirección de instalación no válida',
        fr: "Adresse d'installation invalide"
      },
      description: {
        pt: 'A morada de instalação selecionada não é válida. Por favor, selecione uma morada de instalação válida e tente novamente.',
        en: 'The selected installation address is not valid. Please select a valid installation address and try again.',
        es: 'La dirección de instalación seleccionada no es válida. Por favor, seleccione una dirección de instalación válida e inténtelo de nuevo.',
        fr: "L'adresse d'installation sélectionnée n'est pas valide. Veuillez sélectionner une adresse d'installation valide et réessayer."
      },
      'send-sentry-alert': true
    },
    '01-0043': {
      reason: 'Payment: Payment failed, value could not be captured.',
      'extra-args': ['type', 'fail_reason'],
      title: {
        pt: 'Não foi possível realizar o pagamento',
        en: 'Payment could not be processed',
        es: 'No se pudo realizar el pago',
        fr: "Le paiement n'a pas pu être effectué"
      },
      description: {
        pt: 'A captura do valor da compra falhou, por favor volte a tentar novamente.',
        en: 'The capture of the purchase amount failed, please try again.',
        es: 'La captura del valor de la compra falló, por favor intente nuevamente.',
        fr: "La capture du montant de l'achat a échoué, veuillez réessayer."
      },
      'send-sentry-alert': true
    }
  }
}
